import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { DollarSign, Server, Package, Layers, TrendingUp, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { useClusterMetrics } from '../hooks/useClusterMetrics';

import { parseUtc } from '../lib/parseUtc';
const CostBreakdown = ({ clusterId }) => {
  const [podCosts, setPodCosts] = useState([]);
  const [costProjection, setCostProjection] = useState(null);
  const [nodeHistory, setNodeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Source of truth for the summary cards above — same numbers as Monitoring header.
  const clusterMetrics = useClusterMetrics(clusterId);

  useEffect(() => {
    if (clusterId) {
      fetchAllCostData();
    }
  }, [clusterId]);

  const fetchAllCostData = async () => {
    try {
      setLoading(true);
      
      const [podCostsRes, projectionRes, historyRes] = await Promise.all([
        axios.get(`${API}/pod-costs/${clusterId}`),
        axios.get(`${API}/cost-projection/${clusterId}`),
        axios.get(`${API}/node-history/${clusterId}?days=30`)
      ]);

      setPodCosts(podCostsRes.data.pod_costs || []);
      setCostProjection(projectionRes.data);
      setNodeHistory(historyRes.data.node_changes || []);
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch cost data');
      toast.error('Failed to fetch cost breakdown data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  const getEfficiencyColor = (efficiency) => {
    if (efficiency >= 80) return '#10b981';
    if (efficiency >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  // Prepare data for charts
  const prepareNodeCostData = () => {
    if (!costProjection?.breakdown_by_node) return [];
    
    return Object.entries(costProjection.breakdown_by_node).map(([node, cost]) => ({
      name: node.length > 15 ? `${node.substring(0, 15)}...` : node,
      fullName: node,
      cost: cost,
      formattedCost: formatCurrency(cost)
    }));
  };

  const prepareNamespaceCostData = () => {
    // Primary: aggregate from /pod-costs (always carries `namespace` + per-pod
    // daily cost). Previously this read costProjection.breakdown_by_namespace,
    // which depended on a metrics doc with pod_details — frequently empty,
    // leaving the chart blank.
    if (podCosts && podCosts.length > 0) {
      const totals = podCosts.reduce((acc, pod) => {
        const ns = pod.namespace || 'default';
        acc[ns] = (acc[ns] || 0) + (pod.total_daily_cost || (pod.total_hourly_cost || 0) * 24);
        return acc;
      }, {});
      return Object.entries(totals)
        .map(([namespace, cost]) => ({
          name: namespace,
          cost: Number(cost.toFixed(2)),
          formattedCost: formatCurrency(cost)
        }))
        .sort((a, b) => b.cost - a.cost);
    }
    // Fallback: server-computed breakdown.
    if (costProjection?.breakdown_by_namespace) {
      return Object.entries(costProjection.breakdown_by_namespace)
        .map(([namespace, cost]) => ({
          name: namespace,
          cost: Number(cost),
          formattedCost: formatCurrency(cost)
        }))
        .sort((a, b) => b.cost - a.cost);
    }
    return [];
  };

  const prepareTopPodsByEfficiency = () => {
    return podCosts
      .sort((a, b) => a.efficiency_score - b.efficiency_score)
      .slice(0, 10)
      .map(pod => ({
        name: pod.pod_name.length > 20 ? `${pod.pod_name.substring(0, 20)}...` : pod.pod_name,
        fullName: pod.pod_name,
        efficiency: pod.efficiency_score,
        dailyCost: pod.daily_cost,
        dailyWasteCost: pod.daily_waste_cost || 0,
        namespace: pod.namespace
      }));
  };

  const prepareNodeHistoryChart = () => {
    return nodeHistory.map(entry => ({
      date: parseUtc(entry.timestamp).toLocaleDateString(),
      nodes_added: entry.nodes_added?.length || 0,
      nodes_removed: entry.nodes_removed?.length || 0,
      total_nodes: entry.total_nodes
    }));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-full"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const nodeCostData = prepareNodeCostData();
  const namespaceCostData = prepareNamespaceCostData();
  const topInefficient = prepareTopPodsByEfficiency();
  const nodeHistoryData = prepareNodeHistoryChart();

  return (
    <div className="space-y-6">
      {/* Cost Summary Cards — sourced from useClusterMetrics so the values
          here match the Monitoring header AND the Dashboard. Previously these
          read from /cost-projection (a separate backend computation) and would
          diverge from the live realtime + shadow-engine numbers. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark rounded-lg p-5 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Monthly Projection</p>
            <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-md">
              <DollarSign className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">{formatCurrency(clusterMetrics.monthlyProjection)}</div>
          <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">Current rate × 24 × 30</p>
        </div>

        <div className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark rounded-lg p-5 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Spent Month-to-Date</p>
            <div className="p-2 bg-emerald-50 dark:bg-emerald-500/10 rounded-md">
              <TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">{formatCurrency(clusterMetrics.mtdSpend)}</div>
          <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">
            {clusterMetrics.monthlyProjection > 0
              ? `${((clusterMetrics.mtdSpend / clusterMetrics.monthlyProjection) * 100).toFixed(1)}% of projection`
              : '—'}
          </p>
        </div>

        <div className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark rounded-lg p-5 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Daily Cost</p>
            <div className="p-2 bg-violet-50 dark:bg-violet-500/10 rounded-md">
              <Server className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            </div>
          </div>
          <div className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">{formatCurrency(clusterMetrics.dailyCost)}</div>
          <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">Live: ${(clusterMetrics.costPerHour || 0).toFixed(4)}/hr × 24</p>
        </div>
      </div>
      {costProjection && false && (
        // Old /cost-projection card group retained only for the breakdown
        // tabs below; the header is now driven by useClusterMetrics above.
        null
      )}

      {/* Detailed Breakdown Tabs */}
      <Tabs defaultValue="nodes" className="w-full">
        <TabsList className="grid w-full grid-cols-4 dark:bg-surface-dark">
          <TabsTrigger value="nodes">By Nodes</TabsTrigger>
          <TabsTrigger value="namespaces">By Namespaces</TabsTrigger>
          <TabsTrigger value="pods">Pod Efficiency</TabsTrigger>
          <TabsTrigger value="history">Node History</TabsTrigger>
        </TabsList>

        <TabsContent value="nodes" className="space-y-4">
          <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
            <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
              <CardTitle className="text-sm font-semibold text-slate-700 dark:text-white">Cost Breakdown by Nodes</CardTitle>
            </CardHeader>
            <CardContent>
              {nodeCostData.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={nodeCostData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="cost"
                        >
                          {nodeCostData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium dark:text-white">Node Details</h4>
                    {nodeCostData.map((node, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-surface-border rounded">
                        <span className="text-sm font-medium dark:text-white" title={node.fullName}>{node.name}</span>
                        <span className="text-sm dark:text-text-secondary">{node.formattedCost}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 dark:text-text-secondary">No node cost data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="namespaces" className="space-y-4">
          <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
            <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
              <CardTitle className="text-sm font-semibold text-slate-700 dark:text-white">Cost Breakdown by Namespaces</CardTitle>
            </CardHeader>
            <CardContent className="pt-5">
              {namespaceCostData.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={namespaceCostData}
                        layout="vertical"
                        margin={{ top: 5, right: 16, left: 8, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.6} />
                        <XAxis
                          type="number"
                          stroke="#94a3b8"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(v) => `$${Number(v).toFixed(0)}`}
                        />
                        <YAxis
                          dataKey="name"
                          type="category"
                          width={120}
                          stroke="#64748b"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          formatter={(value) => [formatCurrency(value), 'Daily cost']}
                          contentStyle={{
                            backgroundColor: 'rgba(15, 23, 42, 0.96)',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#f1f5f9'
                          }}
                          labelStyle={{ color: '#f1f5f9' }}
                          cursor={{ fill: 'rgba(99, 102, 241, 0.08)' }}
                        />
                        <Bar dataKey="cost" fill="#6366f1" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary pb-2 border-b border-slate-100 dark:border-surface-border">
                      <span>Namespace</span>
                      <span>Daily Cost</span>
                    </div>
                    {namespaceCostData.map((ns, i) => (
                      <div key={i} className="flex justify-between items-center py-1.5 text-sm">
                        <span className="font-medium text-slate-700 dark:text-white truncate" title={ns.name}>{ns.name}</span>
                        <span className="font-mono tabular-nums text-slate-600 dark:text-text-secondary">{ns.formattedCost}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-10 text-center">
                  <p className="text-sm text-slate-500 dark:text-text-secondary">
                    No namespace cost data available.
                  </p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    Pod-level costs haven't been computed for this cluster yet — try refreshing in a minute or run a fresh sync.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pods" className="space-y-4">
          <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
            <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
              <CardTitle className="text-sm font-semibold text-slate-700 dark:text-white">Least Efficient Pods</CardTitle>
            </CardHeader>
            <CardContent>
              {topInefficient.length > 0 ? (
                <div className="space-y-3">
                  {topInefficient.map((pod, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-surface-border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-sm dark:text-white" title={pod.fullName}>{pod.name}</span>
                          <Badge variant="outline" className="text-xs">{pod.namespace}</Badge>
                        </div>
                        <div className="flex items-center space-x-4 mt-1">
                          <span className="text-xs text-gray-500 dark:text-text-secondary">Daily: {formatCurrency(pod.dailyCost)}</span>
                          {pod.dailyWasteCost > 0 && (
                            <span className="text-xs text-red-500 dark:text-red-400">Waste: {formatCurrency(pod.dailyWasteCost)}</span>
                          )}
                          <div className="flex items-center space-x-1">
                            <span className="text-xs dark:text-text-secondary">Efficiency:</span>
                            <span 
                              className="text-xs font-bold"
                              style={{ color: getEfficiencyColor(pod.efficiency) }}
                            >
                              {(pod.efficiency || 0).toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="w-16 bg-gray-200 dark:bg-background-dark rounded-full h-2">
                        <div 
                          className="h-2 rounded-full" 
                          style={{ 
                            width: `${Math.min(pod.efficiency || 0, 100)}%`,
                            backgroundColor: getEfficiencyColor(pod.efficiency || 0)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-text-secondary">No pod efficiency data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
            <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
              <CardTitle className="text-sm font-semibold text-slate-700 dark:text-white">Node Changes Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              {nodeHistoryData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={nodeHistoryData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line 
                        type="monotone" 
                        dataKey="total_nodes" 
                        stroke="#8884d8" 
                        strokeWidth={2}
                        name="Total Nodes"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="nodes_added" 
                        stroke="#82ca9d" 
                        strokeWidth={2}
                        name="Nodes Added"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="nodes_removed" 
                        stroke="#ff7300" 
                        strokeWidth={2}
                        name="Nodes Removed"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-gray-500 dark:text-text-secondary">No node history data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CostBreakdown;
